// src/providers/data/dataProvider.ts

import { DataProvider } from "@refinedev/core";
import axios, { AxiosInstance } from "axios";
import { stringify } from "query-string";

// 資源類型定義，用於決定 API 路徑
type ResourceType = "imdf" | "projects";

// 資源路徑對照表
const resourcePathMap: Record<
  ResourceType,
  { list: string; detail: string; create: string }
> = {
  imdf: {
    list: "/files",
    detail: "/files",
    create: "/upload",
  },
  projects: {
    list: "",
    detail: "",
    create: "",
  },
};

export const generateDataProvider = (
  apiUrl: string
): Required<DataProvider> => {
  const axiosInstance = axios.create({
    baseURL: apiUrl,
  });

  // 請求攔截器：將 JWT 加入每個請求的 headers
  axiosInstance.interceptors.request.use((config) => {
    // 從 localStorage 獲取 JWT
    const token = localStorage.getItem("token"); // 使用正確的 token key 名稱

    if (token) {
      // 使用 set 方法來設置 Authorization 標頭
      config.headers.set("Authorization", `Bearer ${token}`);

      // 或者使用這種寫法也可以
      // config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  });

  // 決定資源的路徑
  const getResourcePath = (
    resource: string,
    pathType: "list" | "detail" | "create"
  ): string => {
    const resourceType = resource as ResourceType;
    if (resourcePathMap[resourceType]) {
      return resourcePathMap[resourceType][pathType];
    }
    // 預設不加任何路徑
    return "";
  };

  return {
    getApiUrl: () => {
      return apiUrl;
    },
    getList: async ({ resource, filters, sorters }) => {
      const resourcePath = getResourcePath(resource, "list");
      const url = `${apiUrl}/${resource}${resourcePath}`;

      const query = {
        ...(filters && { filters }),
        ...(sorters && { sorters }),
      };

      const queryString = stringify(query);
      const requestUrl = queryString ? `${url}?${queryString}` : url;

      const { data } = await axiosInstance.get(requestUrl);

      return {
        data: data.data,
        total: data.data.length,
      };
    },

    getOne: async ({ resource, id }) => {
      const resourcePath = getResourcePath(resource, "detail");
      const { data } = await axiosInstance.get(
        `${apiUrl}/${resource}${resourcePath}/${id}`
      );

      return {
        data: data.data,
      };
    },

    create: async ({ resource, variables }) => {
      const resourcePath = getResourcePath(resource, "create");
      const { data } = await axiosInstance.post(
        `${apiUrl}/${resource}${resourcePath}`,
        variables
      );

      return {
        data: data.data,
      };
    },

    createMany: async ({ resource, variables }) => {
      const resourcePath = getResourcePath(resource, "create");
      const { data } = await axiosInstance.post(
        `${apiUrl}/${resource}${resourcePath}`,
        variables
      );

      return {
        data: data.data,
      };
    },

    update: async ({ resource, id, variables }) => {
      const resourcePath = getResourcePath(resource, "detail");
      const { data } = await axiosInstance.put(
        `${apiUrl}/${resource}${resourcePath}/${id}`,
        variables
      );

      return {
        data: data.data,
      };
    },

    updateMany: async ({ resource, ids, variables }) => {
      const resourcePath = getResourcePath(resource, "detail");
      const response = await Promise.all(
        ids.map((id) =>
          axiosInstance.put(
            `${apiUrl}/${resource}${resourcePath}/${id}`,
            variables
          )
        )
      );

      return {
        data: response.map((res) => res.data.data),
      };
    },

    deleteOne: async ({ resource, id }) => {
      const resourcePath = getResourcePath(resource, "detail");
      const { data } = await axiosInstance.delete(
        `${apiUrl}/${resource}${resourcePath}/${id}`
      );

      return {
        data: data.data,
      };
    },

    deleteMany: async ({ resource, ids }) => {
      const resourcePath = getResourcePath(resource, "detail");
      const response = await Promise.all(
        ids.map((id) =>
          axiosInstance.delete(`${apiUrl}/${resource}${resourcePath}/${id}`)
        )
      );

      return {
        data: response.map((res) => res.data.data),
      };
    },

    getMany: async ({ resource, ids }) => {
      const resourcePath = getResourcePath(resource, "list");
      const { data } = await axiosInstance.get(
        `${apiUrl}/${resource}${resourcePath}?${stringify({ ids })}`
      );

      return {
        data: data.data,
      };
    },

    custom: async ({
      url,
      method,
      filters,
      sorters,
      payload,
      query,
      headers,
    }) => {
      let requestUrl = `${url}?`;

      if (filters || sorters || query) {
        const queryParams = stringify({
          ...(filters && { filters }),
          ...(sorters && { sorters }),
          ...(query && query),
        });
        requestUrl = `${requestUrl}${queryParams}`;
      }

      const axiosResponse = await axiosInstance.request({
        url: requestUrl,
        method,
        data: payload,
        headers,
      });

      return {
        data: axiosResponse.data,
      };
    },
  };
};
